import { PackageItemState } from '../types/lender-packages.types';
import { NetsuiteItemResponse, NetsuiteItemState } from '../types/netsuite-items.types';
import { ENPHASE_ACCESSORY_ITEMS } from '../constants/netsuite-items.constants';
import {
  BOM_CSV_COLUMNS,
  BOM_CSV_HEADERS,
  ESS_CONFIG_OPTIONS,
  EssConfigValue,
} from '../constants/lender-packages.constants';

/** Raised when an uploaded file cannot be parsed as a NetSuite BOM export CSV */
export class BomCsvImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BomCsvImportError';
  }
}

/** One data row of the BOM export CSV */
export interface ParsedBomCsvRow {
  /** bom_sku (package item) or external_id (NetSuite item) */
  itemId: string;
  quantity: number;
}

/** Parsed content of a BOM export CSV */
export interface ParsedBomCsv {
  lisNumber: string;
  essConfig: EssConfigValue | '';
  packageKey: string;
  rows: ParsedBomCsvRow[];
}

/** Result of overlaying imported quantities onto mapped package items */
export interface BomCsvApplyResult {
  items: PackageItemState[];
  /** Item IDs from the CSV that matched a package bom_sku */
  matchedSkus: Set<string>;
}

/** Result of matching leftover CSV rows against the client NetSuite catalog */
export interface NetsuiteCsvMatchResult {
  matched: NetsuiteItemState[];
  /** CSV Item IDs that matched neither a package bom_sku nor a catalog external_id */
  unmatchedItemIds: string[];
}

/** Input for matching leftover CSV rows against the client NetSuite catalog */
export interface NetsuiteCsvMatchInput {
  /** All parsed CSV rows */
  rows: ParsedBomCsvRow[];
  /** Item IDs already consumed by package items */
  matchedSkus: Set<string>;
  /** Client NetSuite catalog */
  catalogItems: NetsuiteItemResponse[];
  /** Whether the project currently qualifies for auto-managed Enphase accessories */
  isEnphaseProject: boolean;
}

/**
 * Parses one CSV line honoring double-quoted fields with escaped quotes.
 *
 * @param line - Raw CSV line
 * @returns Field values in column order
 */
const parseCsvLine = (line: string): string[] => {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      fields.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  fields.push(current);
  return fields;
};

/**
 * Maps an ESS config CSV value (label or raw value) back to its stored value.
 *
 * @param essField - ESS Config field from the CSV
 * @returns Matching config value, or empty string when unrecognized
 */
const resolveEssConfig = (essField: string): EssConfigValue | '' => {
  const normalized = essField.trim().toLowerCase();
  const option = ESS_CONFIG_OPTIONS.find(
    o => o.label.toLowerCase() === normalized || o.value === normalized,
  );
  return option?.value ?? '';
};

/**
 * Parses a NetSuite BOM export CSV (the file produced by "Export to NetSuite").
 *
 * @param csvText - Raw CSV file content
 * @returns Parsed shared fields and item rows
 * @throws {BomCsvImportError} When the file is empty, lacks the expected columns, or has no item rows
 */
export const parseBomCsv = (csvText: string): ParsedBomCsv => {
  const lines = csvText.split(/\r?\n/).filter(line => line.trim().length > 0);
  if (lines.length < 2) {
    throw new BomCsvImportError('The CSV file is empty or has no data rows');
  }

  const header = parseCsvLine(lines[0]).map(h => h.trim());
  const columnIndexes = new Map<string, number>(
    BOM_CSV_HEADERS.map(name => [name, header.indexOf(name)]),
  );
  const missingColumns = BOM_CSV_HEADERS.filter(name => (columnIndexes.get(name) ?? -1) < 0);
  if (missingColumns.length > 0) {
    throw new BomCsvImportError(`Not a BOM export CSV — missing columns: ${missingColumns.join(', ')}`);
  }

  const fieldAt = (fields: string[], column: string): string =>
    (fields[columnIndexes.get(column) ?? -1] ?? '').trim();

  const rows: ParsedBomCsvRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const fields = parseCsvLine(lines[i]);
    const itemId = fieldAt(fields, BOM_CSV_COLUMNS.ITEM_ID);
    if (!itemId) continue;
    const quantity = Math.max(0, parseInt(fieldAt(fields, BOM_CSV_COLUMNS.QTY), 10) || 0);
    rows.push({ itemId, quantity });
  }
  if (rows.length === 0) {
    throw new BomCsvImportError('No item rows found in the CSV');
  }

  const firstRowFields = parseCsvLine(lines[1]);
  return {
    lisNumber: fieldAt(firstRowFields, BOM_CSV_COLUMNS.LIS_NUMBER),
    essConfig: resolveEssConfig(fieldAt(firstRowFields, BOM_CSV_COLUMNS.ESS_CONFIG)),
    packageKey: fieldAt(firstRowFields, BOM_CSV_COLUMNS.PACKAGE_KEY),
    rows,
  };
};

/**
 * Overlays imported CSV quantities onto freshly mapped package items.
 * BOM items whose bom_sku appears in the CSV are enabled with the CSV quantity;
 * all other BOM items are disabled. Parent items stay enabled while any child is.
 *
 * @param items - Package items freshly mapped from the selected package
 * @param skuQuantities - CSV Item ID → quantity
 * @returns Updated items and the set of matched SKUs
 */
export const applyImportedQuantities = (
  items: PackageItemState[],
  skuQuantities: Map<string, number>,
): BomCsvApplyResult => {
  const matchedSkus = new Set<string>();
  const updated = items.map(item => {
    const bomItems = item.bom_items.map(bomItem => {
      const sku = bomItem.bom_sku;
      const quantity = sku ? skuQuantities.get(sku) : undefined;
      if (!sku || quantity === undefined) {
        return { ...bomItem, enabled: false };
      }
      matchedSkus.add(sku);
      return { ...bomItem, enabled: true, quantity };
    });
    return { ...item, enabled: bomItems.some(b => b.enabled), bom_items: bomItems };
  });
  return { items: updated, matchedSkus };
};

/**
 * Matches CSV rows that did not correspond to package SKUs against the
 * client NetSuite catalog (by external_id).
 *
 * On Enphase projects, Q-Cable accessory items are flagged auto_added so they
 * render in the Enphase section and stay managed by the Enphase sync. On
 * non-Enphase projects they stay manual — flagging them auto_added there would
 * hide them from every section while the sync later removes them.
 *
 * @param input - CSV rows, consumed SKUs, catalog, and Enphase qualification
 * @returns NetSuite item states to add and any still-unmatched Item IDs
 */
export const matchNetsuiteItems = (input: NetsuiteCsvMatchInput): NetsuiteCsvMatchResult => {
  const { rows, matchedSkus, catalogItems, isEnphaseProject } = input;
  const matched: NetsuiteItemState[] = [];
  const unmatchedItemIds: string[] = [];
  const enphaseExternalIds = new Set<string>(ENPHASE_ACCESSORY_ITEMS.map(a => a.externalId));

  for (const row of rows) {
    if (matchedSkus.has(row.itemId)) continue;
    const catalogItem = catalogItems.find(c => c.external_id === row.itemId);
    if (!catalogItem) {
      unmatchedItemIds.push(row.itemId);
      continue;
    }
    matched.push({
      id: catalogItem.id,
      external_id: catalogItem.external_id,
      name: catalogItem.name,
      display_name: catalogItem.display_name,
      item_category: catalogItem.item_category,
      manufacturer: catalogItem.manufacturer,
      quantity: row.quantity,
      enabled: true,
      auto_added: isEnphaseProject && enphaseExternalIds.has(catalogItem.external_id),
      quantity_edited: true,
    });
  }

  return { matched, unmatchedItemIds };
};
